/**
 * Facet API 服务
 * v9.1 新增：场景人格面（Facet）管理
 * 
 * 调用路径：前端 -> Agent Admin -> Identity Engine
 * 参考文档：docs/shared/api-protocol/identity-engine-api.md
 * - 第 4.6 节：获取被学习人列表
 * - 第 8 节：Facet 管理接口
 * - 第 9 节：Quality Gate 接口
 */

import { ApiClient, apiClients } from '@/shared/services/api'
import type { SuccessResponse } from '@/shared/types/api'

// 简单的消息响应类型（删除操作返回）
interface MessageResponse {
  message: string
}
import type {
  // Facet 相关
  FacetListResponse,
  FacetDetail,
  FacetLearningStatus,
  UpdateFacetRequest,
  PublishFacetRequest,
  PublishFacetResponse,
  LearnFacetRequest,
  LearnFacetResponse,
  // Quality Gate 相关
  TestCaseListResponse,
  TestCase,
  CreateTestCaseRequest,
  UpdateTestCaseRequest,
  RunTestsRequest,
  RunTestsResponse,
  TestResultResponse,
  // 被学习人相关
  TargetPersonListResponse,
  SceneTag,
  // v9.2 语料-场景关联相关
  FacetCorpusListResponse,
  AssociateCorpusRequest,
  AssociateCorpusResponse,
  LearnAllRequest,
  LearnAllResponse,
} from '@/modules/identity/types/facet'

/**
 * Identity Engine API 基础路径
 * 由 Agent Admin 代理转发
 */
const BASE_PATH = '/identity'

/**
 * Facet API 服务
 */
export class FacetApiService {
  // ==================== 被学习人管理（4.6 节）====================

  /**
   * 获取被学习人列表
   * GET /api/v1/identity-engine/identities/{identity_id}/target-persons
   * 
   * 参考：API 文档 4.6 节
   */
  static async listTargetPersons(
    identityId: string
  ): Promise<SuccessResponse<TargetPersonListResponse>> {
    return ApiClient.getWithClient<TargetPersonListResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/target-persons`
    )
  }

  // ==================== Facet 管理（第 8 节）====================

  /**
   * 获取 Facet 列表
   * GET /api/v1/identity-engine/identities/{identity_id}/facets
   * 
   * 参考：API 文档 8.1 节
   */
  static async listFacets(
    identityId: string
  ): Promise<SuccessResponse<FacetListResponse>> {
    return ApiClient.getWithClient<FacetListResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets`
    )
  }

  /**
   * 获取 Facet 详情
   * GET /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}
   * 
   * 参考：API 文档 8.2 节
   */
  static async getFacet(
    identityId: string,
    sceneId: SceneTag
  ): Promise<SuccessResponse<FacetDetail>> {
    return ApiClient.getWithClient<FacetDetail>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}`
    )
  }

  /**
   * 更新 Facet（编辑 injection_pack）
   * PATCH /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}
   * 
   * 参考：API 文档 8.3 节
   */
  static async updateFacet(
    identityId: string,
    sceneId: SceneTag,
    data: UpdateFacetRequest
  ): Promise<SuccessResponse<{ facet_id: string; scene_tag: SceneTag; status: string; message: string }>> {
    return ApiClient.patchWithClient(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}`,
      data
    )
  }

  /**
   * 发布 Facet
   * POST /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}/publish
   * 
   * 参考：API 文档 8.4 节
   */
  static async publishFacet(
    identityId: string,
    sceneId: SceneTag,
    data?: PublishFacetRequest
  ): Promise<SuccessResponse<PublishFacetResponse>> {
    return ApiClient.postWithClient<PublishFacetResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/publish`,
      data || {}
    )
  }

  /**
   * 重置 Facet（删除学习结果，回归未学习状态）
   * DELETE /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}
   * 
   * 参考：v9.2.2 新增
   */
  static async resetFacet(
    identityId: string,
    sceneId: SceneTag
  ): Promise<SuccessResponse<MessageResponse>> {
    return ApiClient.deleteWithClient<MessageResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}`
    )
  }

  /**
   * 触发 Facet 学习
   * POST /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}/learn
   * 
   * 参考：API 文档 8.5 节
   */
  static async learnFacet(
    identityId: string,
    sceneId: SceneTag,
    data?: LearnFacetRequest
  ): Promise<SuccessResponse<LearnFacetResponse>> {
    return ApiClient.postWithClient<LearnFacetResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/learn`,
      data || {}
    )
  }

  /**
   * 获取 Facet 学习状态
   * GET /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}/learn/status
   * 
   * 参考：API 文档 8.6 节
   */
  static async getFacetLearningStatus(
    identityId: string,
    sceneId: SceneTag
  ): Promise<SuccessResponse<FacetLearningStatus>> {
    return ApiClient.getWithClient<FacetLearningStatus>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/learn/status`
    )
  }

  // ==================== Quality Gate（第 9 节）====================

  /**
   * 获取测试用例列表
   * GET /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}/test-cases
   * 
   * 参考：API 文档 9.1 节
   */
  static async listTestCases(
    identityId: string,
    sceneId: SceneTag,
    params?: {
      type?: string
      status?: string
      page?: number
      page_size?: number
    }
  ): Promise<SuccessResponse<TestCaseListResponse>> {
    return ApiClient.getWithClient<TestCaseListResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/test-cases`,
      { params }
    )
  }

  /**
   * 创建测试用例
   * POST /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}/test-cases
   * 
   * 参考：API 文档 9.2 节
   */
  static async createTestCase(
    identityId: string,
    sceneId: SceneTag,
    data: CreateTestCaseRequest
  ): Promise<SuccessResponse<TestCase>> {
    return ApiClient.postWithClient<TestCase>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/test-cases`,
      data
    )
  }

  /**
   * 更新测试用例
   * PUT /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}/test-cases/{case_id}
   * 
   * 参考：API 文档 9.3 节
   */
  static async updateTestCase(
    identityId: string,
    sceneId: SceneTag,
    caseId: string,
    data: UpdateTestCaseRequest
  ): Promise<SuccessResponse<TestCase>> {
    return ApiClient.putWithClient<TestCase>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/test-cases/${caseId}`,
      data
    )
  }

  /**
   * 删除测试用例
   * DELETE /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}/test-cases/{case_id}
   * 
   * 参考：API 文档 9.4 节
   */
  static async deleteTestCase(
    identityId: string,
    sceneId: SceneTag,
    caseId: string
  ): Promise<SuccessResponse<MessageResponse>> {
    return ApiClient.deleteWithClient<MessageResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/test-cases/${caseId}`
    )
  }

  /**
   * 运行测试
   * POST /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}/run-tests
   * 
   * 参考：API 文档 9.5 节
   */
  static async runTests(
    identityId: string,
    sceneId: SceneTag,
    data?: RunTestsRequest
  ): Promise<SuccessResponse<RunTestsResponse>> {
    return ApiClient.postWithClient<RunTestsResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/run-tests`,
      data || {}
    )
  }

  /**
   * 获取测试结果
   * GET /api/v1/identity-engine/identities/{identity_id}/facets/{scene_id}/test-results
   * 
   * 参考：API 文档 9.6 节
   */
  static async getTestResults(
    identityId: string,
    sceneId: SceneTag,
    jobId?: string
  ): Promise<SuccessResponse<TestResultResponse>> {
    return ApiClient.getWithClient<TestResultResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneId}/test-results`,
      { params: jobId ? { job_id: jobId } : undefined }
    )
  }

  // ==================== v9.2 语料-场景关联接口 ====================

  /**
   * 获取场景已关联的语料列表
   * GET /api/v1/identity/identities/{identity_id}/facets/{scene_tag}/corpus
   * 
   * 参考：v9.2 API 文档
   */
  static async listFacetCorpus(
    identityId: string,
    sceneTag: SceneTag
  ): Promise<SuccessResponse<FacetCorpusListResponse>> {
    return ApiClient.getWithClient<FacetCorpusListResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneTag}/corpus`
    )
  }

  /**
   * 添加语料到场景
   * POST /api/v1/identity/identities/{identity_id}/facets/{scene_tag}/corpus
   * 
   * 参考：v9.2 API 文档
   */
  static async associateCorpus(
    identityId: string,
    sceneTag: SceneTag,
    data: AssociateCorpusRequest
  ): Promise<SuccessResponse<AssociateCorpusResponse>> {
    return ApiClient.postWithClient<AssociateCorpusResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneTag}/corpus`,
      data
    )
  }

  /**
   * 从场景移除语料
   * DELETE /api/v1/identity/identities/{identity_id}/facets/{scene_tag}/corpus/{corpus_id}
   * 
   * 参考：v9.2 API 文档
   */
  static async disassociateCorpus(
    identityId: string,
    sceneTag: SceneTag,
    corpusId: string
  ): Promise<SuccessResponse<{ message: string }>> {
    return ApiClient.deleteWithClient<{ message: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneTag}/corpus/${corpusId}`
    )
  }

  /**
   * 全局学习（学习所有有语料的场景）
   * POST /api/v1/identity/identities/{identity_id}/learn/all
   * 
   * 参考：v9.2 API 文档
   */
  static async learnAll(
    identityId: string,
    data?: LearnAllRequest
  ): Promise<SuccessResponse<LearnAllResponse>> {
    return ApiClient.postWithClient<LearnAllResponse>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/learn/all`,
      data || {}
    )
  }

  // ==================== 手动测试接口 ====================

  /**
   * 手动测试 Facet
   * POST /api/v1/identity/identities/{identity_id}/facets/{scene_tag}/test
   * 
   * 参考：v9.2 API 文档
   */
  static async manualTest(
    identityId: string,
    sceneTag: SceneTag,
    data: { input: string }
  ): Promise<SuccessResponse<{ output?: string; response?: string }>> {
    return ApiClient.postWithClient<{ output?: string; response?: string }>(
      apiClients.agentAdmin,
      `${BASE_PATH}/identities/${identityId}/facets/${sceneTag}/test`,
      data
    )
  }
}
